/**
 * The ONE registry of measurement sources a learner performance report can be filtered by.
 *
 * <p>This file is kept BYTE-IDENTICAL in three repos — catchup-app-web, catchup-mobile and
 * catchup-admin-web (`src/utilization/LearnerSourceDefs.ts`) — so the learner's own report and the
 * staff's view of the same learner can never disagree on what a "Kaynak" is. Change it in all three
 * at once, with each repo's test.
 *
 * <p>Before this existed, the learner detail modal carried three independent controls for this single
 * axis, each with its own list: the tab strip named two sources, the gelişim chart's strip named four,
 * and the outcome tree's chips named four ending in a DIFFERENT fourth. The same tab therefore gave
 * two answers to "what can I filter by", and the chart and the tree disagreed the moment either was
 * touched. Fixing the lists to agree once would only have them drift again, so the enumeration lives
 * here and every consumer reads it.
 *
 * <p>Five kinds, in this order everywhere (PO decision, holistic reports): Şube ödevi, Destek ödevi,
 * Bireysel ödev, Yazılı sınav, Deneme sınavı. The optical exam is retired and is no longer a source.
 * `group` splits them into the two report tabs that list work — Ödevler (`assignment`) and Sınavlar
 * (`exam`) — while Genel durum folds all five together.
 *
 * <p>`defaultVisible` decides what the report OPENS on, and it is a product decision. Every source
 * ships ticked: the report's job is to show what is known about the learner, and a chip that starts
 * off hides evidence from the reader who did not think to look for it.
 *
 * <p>`alwaysShown` is what makes the filter honest about provenance. A learner with şube data alone
 * would otherwise get no chip at all, and a teacher reading 70% could not tell whether destek or
 * bireysel ödev was inside that number. The three ödev sources therefore always render — disabled,
 * with a 0, when the learner has none — because their absence is the very thing a reader needs told.
 * The exam sources appear only when there is something to show.
 *
 * <p>Every helper takes an optional `scopeIds`: the sources one tab actually consumes (e.g. Güçlü ve
 * zayıf yönler reads only the time-bearing ödev kinds, Sınavlar only the two exams). The selection is
 * ONE axis shared by every tab; a scope only narrows what a tab shows and refuses to let that tab be
 * emptied by its own chips.
 */
export type LearnerSourceGroup = "assignment" | "exam";

export interface LearnerSourceDef {
  id: string;
  /**
   * i18n key for the Kaynak chip. Short — the row's own label carries the "performansı" sense,
   * and these are the app's EXISTING names for these things rather than new duplicates of strings it
   * already ships.
   */
  labelKey: string;
  defaultVisible: boolean;
  alwaysShown: boolean;
  group: LearnerSourceGroup;
}

export const LEARNER_SOURCE_DEFS: LearnerSourceDef[] = [
  {
    id: "BRANCH_ASSIGNMENT",
    labelKey: "corporate_assessment_assignment",
    defaultVisible: true,
    alwaysShown: true,
    group: "assignment",
  },
  {
    id: "SUPPORT_ASSIGNMENT",
    labelKey: "support_assignment",
    defaultVisible: true,
    alwaysShown: true,
    group: "assignment",
  },
  {
    id: "PERSONALIZED_ASSIGNMENT",
    labelKey: "corporate_personalized_assignment",
    defaultVisible: true,
    alwaysShown: true,
    group: "assignment",
  },
  {
    id: "HANDWRITTEN_EXAM",
    labelKey: "handwritten_exam",
    defaultVisible: true,
    alwaysShown: false,
    group: "exam",
  },
  {
    id: "MOCK_EXAM",
    labelKey: "mock_exam",
    defaultVisible: true,
    alwaysShown: false,
    group: "exam",
  },
];

/**
 * The sources Güçlü ve zayıf yönler can fold: the kinds whose answers carry a solving time. Exams are
 * sat on paper (or imported), so they have no time axis to place an outcome on.
 */
export const STRONG_WEAK_SOURCE_IDS: string[] = [
  "BRANCH_ASSIGNMENT",
  "SUPPORT_ASSIGNMENT",
  "PERSONALIZED_ASSIGNMENT",
];

const DEF_BY_ID: Record<string, LearnerSourceDef> = LEARNER_SOURCE_DEFS.reduce(
  (acc, def) => {
    acc[def.id] = def;
    return acc;
  },
  {} as Record<string, LearnerSourceDef>,
);

export const retrieveLearnerSourceDef = (
  id: string,
): LearnerSourceDef | undefined => DEF_BY_ID[id];

/** The ids of one group, in registry order — the Kaynak scope of the Ödevler or Sınavlar tab. */
export const retrieveLearnerSourceIdsByGroup = (
  group: LearnerSourceGroup,
): string[] =>
  LEARNER_SOURCE_DEFS.filter((def) => def.group === group).map((def) => def.id);

/**
 * Canonicalises a set of ids: registry order, de-duplicated, unknown ids dropped.
 *
 * <p>Order matters because these ids drive a chip ROW — a set that reordered itself as the reader
 * ticked things would make the row unscannable, and the chart's lanes read in this order too.
 */
export const orderLearnerSourceIds = (ids: string[]): string[] => {
  const wanted = new Set(ids || []);
  return LEARNER_SOURCE_DEFS.filter((def) => wanted.has(def.id)).map(
    (def) => def.id,
  );
};

/** `ids` narrowed to `scopeIds`; no scope means every source. */
const withinScope = (ids: string[], scopeIds?: string[] | null): string[] =>
  scopeIds == null
    ? orderLearnerSourceIds(ids)
    : orderLearnerSourceIds(ids).filter((id) => scopeIds.includes(id));

/**
 * The sources a report should open with, given what the learner actually has.
 *
 * <p>Falls back to every available source when none of the defaults have data — otherwise a learner
 * whose only work is a yazılı sınav would open on an empty report, which reads as "this learner has
 * done nothing" rather than "your default filter excludes what they did".
 */
export const resolveDefaultVisibleSourceIds = (
  availableIds: string[],
  scopeIds?: string[] | null,
): string[] => {
  const available = withinScope(availableIds, scopeIds);
  const preferred = available.filter((id) => DEF_BY_ID[id]?.defaultVisible);
  return preferred.length > 0 ? preferred : available;
};

/**
 * The selection in force: the reader's own choice where they have made one, the default until then.
 *
 * <p>Pass `null` for "untouched". A stored choice is intersected with what is available (and with the
 * scope) and falls back to the default when nothing survives, so a selection made against one learner
 * — or on another tab — cannot render the next one blank.
 */
export const resolveVisibleSourceIds = (
  selectedIds: string[] | null | undefined,
  availableIds: string[],
  scopeIds?: string[] | null,
): string[] => {
  const available = withinScope(availableIds, scopeIds);
  if (available.length === 0) return [];
  if (selectedIds == null) return resolveDefaultVisibleSourceIds(available);
  const kept = orderLearnerSourceIds(selectedIds).filter((id) =>
    available.includes(id),
  );
  return kept.length > 0 ? kept : resolveDefaultVisibleSourceIds(available);
};

/**
 * Whether a source may be unticked — false for the last one standing.
 *
 * <p>The empty selection is refused at the control rather than handled downstream: with every source
 * off, the graphs fell through to the generic "veri bulunamadı" empty state, which claims something
 * false about the learner and offers no way back. Making the last chip un-untickable means that state
 * cannot be reached at all.
 */
export const canDeselectLearnerSource = (
  visibleIds: string[],
  id: string,
): boolean => (visibleIds || []).includes(id) && (visibleIds || []).length > 1;

/**
 * Toggles one source, keeping the result canonical and never empty (within the scope, when given).
 *
 * <p>An unavailable source is inert: its chip is rendered disabled, and a toggle that arrived anyway
 * (a stale event, a keyboard repeat) must not put an id with no data into the selection. Sources
 * outside the scope keep whatever state they had — a tab's chips only move that tab's sources.
 */
export const toggleLearnerSourceId = (
  selectedIds: string[] | null | undefined,
  id: string,
  availableIds: string[],
  scopeIds?: string[] | null,
): string[] => {
  const availableAll = orderLearnerSourceIds(availableIds);
  const currentAll = resolveVisibleSourceIds(selectedIds, availableAll);
  if (scopeIds != null && !scopeIds.includes(id)) return currentAll;
  if (!availableAll.includes(id)) return currentAll;
  const currentScoped = resolveVisibleSourceIds(selectedIds, availableAll, scopeIds);
  if (currentScoped.includes(id)) {
    if (!canDeselectLearnerSource(currentScoped, id)) return currentAll;
    // The scoped view may have fallen back to its default (nothing of the stored choice was in
    // scope); what it SHOWS is what the reader is narrowing, so it is kept whole minus this one.
    return orderLearnerSourceIds([
      ...currentAll.filter((item) => scopeIds != null && !scopeIds.includes(item)),
      ...currentScoped.filter((item) => item !== id),
    ]);
  }
  return orderLearnerSourceIds([...currentAll, ...currentScoped, id]);
};

/**
 * The chips to render, in row order, each already carrying the state its visual needs.
 *
 * <p>Built here rather than in the component so the "always shown even when empty" rule and the
 * disabled reasons are decided once, under test, instead of in JSX in three frontends.
 */
export interface LearnerSourceChip {
  id: string;
  labelKey: string;
  checked: boolean;
  /** No data for this learner — the chip is a read-out, not a control. */
  unavailable: boolean;
  /** Checked and alone: unticking it would empty the report (or the tab, within a scope). */
  lockedOn: boolean;
}

export const buildLearnerSourceChipList = (
  selectedIds: string[] | null | undefined,
  availableIds: string[],
  scopeIds?: string[] | null,
): LearnerSourceChip[] => {
  const available = withinScope(availableIds, scopeIds);
  const visible = resolveVisibleSourceIds(selectedIds, availableIds, scopeIds);
  return LEARNER_SOURCE_DEFS.filter(
    (def) => scopeIds == null || scopeIds.includes(def.id),
  )
    .filter((def) => def.alwaysShown || available.includes(def.id))
    .map((def) => {
      const isAvailable = available.includes(def.id);
      const checked = visible.includes(def.id);
      return {
        id: def.id,
        labelKey: def.labelKey,
        checked,
        unavailable: !isAvailable,
        lockedOn: checked && !canDeselectLearnerSource(visible, def.id),
      };
    });
};

/**
 * True when the reader is looking at less than everything they could — the cue a surface uses to say
 * so, rather than letting a narrowed view pass for the whole picture.
 */
export const isLearnerSourceSelectionNarrowed = (
  selectedIds: string[] | null | undefined,
  availableIds: string[],
  scopeIds?: string[] | null,
): boolean => {
  const available = withinScope(availableIds, scopeIds);
  return (
    resolveVisibleSourceIds(selectedIds, availableIds, scopeIds).length <
    available.length
  );
};
